// src/pages/BindLocation.jsx
import { useState } from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Alert,
  Paper,
  Stack,
  Card,
  Grid,
  InputAdornment,
  Zoom,
  Slide,
  Divider,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  QrCode as QrCodeIcon,
  LocationOn as LocationIcon,
  Link as LinkIcon,
  Help as HelpIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

export default function BindLocation() {
  const [qrId, setQrId] = useState('');
  const [location, setLocation] = useState('');
  const [success, setSuccess] = useState(false);
  const theme = useTheme();

  const submit = async () => {
    try {
      await fetch('/api/bind-location', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qrId, location_note: location }),
      });
      setSuccess(true);
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
      
      // Clear the form after successful submission
      setQrId('');
      setLocation('');
    } catch (error) {
      console.error('Error binding location:', error);
    }
  };

  return (
    <Box>
      <Box mb={4} display="flex" alignItems="center">
        <LinkIcon 
          fontSize="large" 
          sx={{ 
            mr: 2, 
            color: 'primary.main',
            bgcolor: 'primary.lighter', 
            p: 1, 
            borderRadius: '50%' 
          }} 
        />
        <Typography variant="h4" component="h2" gutterBottom>
          Bind QR Code to Location
        </Typography>
      </Box>
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Card elevation={3} sx={{ borderRadius: theme.shape.borderRadius }}>
            <Box sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
              <Typography variant="h6" gutterBottom>
                Link QR Code to Physical Location
              </Typography>
              <Typography variant="body2">
                Assign a physical location description to a QR code to easily track where each code is deployed.
              </Typography>
            </Box>
            
            <Box sx={{ p: 3 }}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="QR ID"
                  value={qrId}
                  onChange={(e) => setQrId(e.target.value)}
                  variant="outlined"
                  placeholder="Enter QR ID"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <QrCodeIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Location Note"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  variant="outlined"
                  placeholder="Enter Location Details (e.g., 'Main Entrance', 'Conference Room B', 'Product Display #4')"
                  multiline
                  rows={3}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={submit}
                  disabled={!qrId || !location}
                  sx={{ 
                    mt: 2,
                    py: 1.5,
                    fontWeight: 600,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      top: 0,
                      left: 0,
                      background: 'linear-gradient(120deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 70%)',
                      transition: 'all 0.6s ease',
                      transform: 'translateX(-100%)'
                    },
                    '&:hover::after': {
                      transform: 'translateX(100%)'
                    }
                  }}
                  startIcon={<LinkIcon />}
                >
                  Bind Location
                </Button>
                
                <Zoom in={success} timeout={500} unmountOnExit>
                  <Alert 
                    icon={<CheckCircleIcon fontSize="inherit" />}
                    severity="success" 
                    sx={{ mt: 2, alignItems: 'center' }}
                  >
                    Location successfully bound to QR code!
                  </Alert>
                </Zoom>
              </Stack>
            </Box>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={5}>
          <Card 
            elevation={2} 
            sx={{ 
              height: '100%', 
              bgcolor: 'rgba(0,0,0,0.02)', 
              display: 'flex', 
              flexDirection: 'column'
            }}
          >
            <Box sx={{ p: 3, bgcolor: 'secondary.main', color: 'white' }}>
              <Box display="flex" alignItems="center">
                <HelpIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Help & Information
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ p: 3, flexGrow: 1 }}>
              <Typography variant="body1" paragraph>
                Use this form to associate a QR code with its physical location.
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                What is a QR ID?
              </Typography>
              <Typography variant="body2" paragraph>
                The QR ID is the unique identifier of the QR code, typically found in the URL or printed on the QR code label.
              </Typography>
              
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Location Note Examples:
              </Typography>
              <Typography variant="body2" component="div">
                <ul>
                  <li>Building A, Main Entrance</li>
                  <li>Trade Show Booth #42</li>
                  <li>Conference Room "Maple"</li>
                  <li>Product Display - Electronics Section</li>
                </ul>
              </Typography>
              
              <Box sx={{ mt: 2, bgcolor: 'info.lighter', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="info.dark">
                  <strong>Tip:</strong> Be as specific as possible with your location descriptions to make it easier to track QR codes in the future.
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
